/**
 * La pile des <dialog> modales ouvertes, dans l'ordre d'ouverture — que le navigateur ne donne pas
 * (`:modal` dit qu'une dialog est modale, pas laquelle est au-dessus). Un MutationObserver sur
 * l'attribut `open` la tient dès le premier abonnement ; une dialog retirée du DOM encore ouverte
 * (navigation, frame remplacé) en sort aussi, sans événement `close`.
 */
type Listener = (top: HTMLDialogElement | null) => void;

const stack: HTMLDialogElement[] = [];
const listeners = new Set<Listener>();
let observer: MutationObserver | null = null;

/** La modale du dessus : la seule qui ne soit pas inerte. */
export function topModal(): HTMLDialogElement | null {
  return stack.at(-1) ?? null;
}

/** Appelle `listener` à chaque changement de modale du dessus. Rend le désabonnement. */
export function onTopModalChange(listener: Listener): () => void {
  start();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const isModal = (node: Node): node is HTMLDialogElement =>
  node instanceof HTMLDialogElement && node.isConnected && node.matches(":modal");

function start(): void {
  if (observer) return;

  // Déjà ouvertes au premier abonnement : l'ordre du document, à défaut de mieux.
  document.querySelectorAll("dialog").forEach((dialog) => {
    if (isModal(dialog)) stack.push(dialog);
  });
  observer = new MutationObserver(update);
  observer.observe(document, { subtree: true, childList: true, attributes: true, attributeFilter: ["open"] });
}

function update(records: MutationRecord[]): void {
  const before = topModal();

  for (let i = stack.length - 1; i >= 0; i--) {
    if (!isModal(stack[i])) stack.splice(i, 1);
  }
  for (const record of records) {
    if (record.type === "attributes" && isModal(record.target) && !stack.includes(record.target)) {
      stack.push(record.target);
    }
  }

  const top = topModal();
  if (top !== before) listeners.forEach((listener) => listener(top));
}
